/**
 * Paginación sobre cargas progresivas de admin-data.
 * El backend no responde con rangos largos, así que una página "global" se arma
 * pidiendo solo las páginas de los bloques mensuales donde caen sus filas.
 */

export const DEFAULT_PER_PAGE = 10

/**
 * Totales de cada paginador de una respuesta mensual; sirven para ubicar páginas globales.
 * @param {object} monthData respuesta `data` de admin-data para un bloque
 * @returns {{ rows: number, perPage: number, campaigns: Record<string, number> }}
 */
export function extractPaginatorTotals(monthData) {
  const campaigns = {}
  Object.entries(monthData?.CAMPAIGN_CUPONS || {}).forEach(([key, value]) => {
    campaigns[key] = Number(value?.CUPONS_PAGINATE?.total || 0)
  })
  return {
    rows: Number(monthData?.ROWS_PAGINATE?.total || 0),
    perPage: Number(monthData?.ROWS_PAGINATE?.per_page || 0) || DEFAULT_PER_PAGE,
    campaigns,
  }
}

/**
 * Páginas locales (y el tramo de filas de cada una) que cubren la página global.
 * @returns {Array<{ blockIndex: number, localPage: number, from: number, to: number }>}
 */
function planGlobalPage(totals, page, perPage) {
  const start = (page - 1) * perPage
  const end = start + perPage
  const plan = []
  let offset = 0

  totals.forEach((total, blockIndex) => {
    const blockStart = offset
    offset += total
    const from = Math.max(start, blockStart)
    const to = Math.min(end, offset)
    if (from >= to) return

    let local = from - blockStart
    const localEnd = to - blockStart
    while (local < localEnd) {
      const localPage = Math.floor(local / perPage) + 1
      const pageStart = (localPage - 1) * perPage
      const sliceEnd = Math.min(localEnd, pageStart + perPage)
      plan.push({ blockIndex, localPage, from: local - pageStart, to: sliceEnd - pageStart })
      local = sliceEnd
    }
  })

  return plan
}

function buildPaginator(data, page, perPage, total) {
  const firstIndex = (page - 1) * perPage
  return {
    current_page: page,
    data,
    first_page_url: null,
    from: data.length ? firstIndex + 1 : 0,
    last_page: Math.max(1, Math.ceil(total / perPage)),
    last_page_url: null,
    links: [],
    next_page_url: null,
    path: '',
    per_page: perPage,
    prev_page_url: null,
    to: firstIndex + data.length,
    total,
  }
}

const sum = values => values.reduce((acc, value) => acc + value, 0)

/**
 * Arma la página global de la tabla de consultas y/o de los cupones por campaña.
 * @param {Array<{ range: object, totals: ReturnType<typeof extractPaginatorTotals> }>} blocks
 * @param {number} page página global (1-based)
 * @param {(range: object, localPage: number) => Promise<object>} fetchBlockPage devuelve `data` del backend
 * @param {{ rows?: boolean, campaigns?: boolean }} targets
 * @returns {Promise<{ ROWS_PAGINATE?: object, CAMPAIGN_CUPONS?: Record<string, { CUPONS_PAGINATE: object }> }>}
 */
export async function loadGlobalPage(
  blocks,
  page,
  fetchBlockPage,
  { rows = true, campaigns = false } = {}
) {
  const perPage = blocks[0]?.totals?.perPage || DEFAULT_PER_PAGE
  const cache = new Map()
  const getBlockPage = (blockIndex, localPage) => {
    const key = `${blockIndex}|${localPage}`
    if (!cache.has(key)) cache.set(key, fetchBlockPage(blocks[blockIndex].range, localPage))
    return cache.get(key)
  }

  const collect = async (totals, pick) => {
    const data = []
    for (const step of planGlobalPage(totals, page, perPage)) {
      const monthData = await getBlockPage(step.blockIndex, step.localPage)
      const list = pick(monthData)?.data
      if (Array.isArray(list)) data.push(...list.slice(step.from, step.to))
    }
    return data
  }

  const result = {}

  if (rows) {
    const totals = blocks.map(block => block.totals.rows)
    const data = await collect(totals, monthData => monthData?.ROWS_PAGINATE)
    result.ROWS_PAGINATE = buildPaginator(data, page, perPage, sum(totals))
  }

  if (campaigns) {
    const keys = new Set()
    blocks.forEach(block => Object.keys(block.totals.campaigns).forEach(key => keys.add(key)))
    result.CAMPAIGN_CUPONS = {}
    for (const key of keys) {
      const totals = blocks.map(block => block.totals.campaigns[key] || 0)
      const data = await collect(totals, monthData => monthData?.CAMPAIGN_CUPONS?.[key]?.CUPONS_PAGINATE)
      result.CAMPAIGN_CUPONS[key] = {
        CUPONS_PAGINATE: buildPaginator(data, page, perPage, sum(totals)),
      }
    }
  }

  return result
}
